// =============================================================================
// Stories do Instagram (1080 x 1920) — 1, 2 ou 3 produtos por story.
//
// Mesmo raciocínio do pôster A3 (CampanhaPoster.js): produtos aprovados da
// campanha, foto tratada no navegador (recorte inteligente), a folha do preço do
// pôster e o preço do banco. A diferença é que aqui tudo se mexe:
// - cada produto (foto + folha) é um bloco: arrasta inteiro e aumenta/diminui
//   pelos cantos;
// - a folha também anda sozinha em volta da foto (alça "preço");
// - o topo e o período arrastam e mudam de tamanho.
// Onde cada produto começa depende de quantos o story tem (helpers/storyLayout.js).
//
// A prévia usa o MESMO CSS do PNG (vem do servidor) e o mesmo ajuste da folha
// (helpers/posterFolha.js): o que aparece aqui é o que sai na imagem.
// =============================================================================
import React, { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { zipSync } from 'fflate';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/400-italic.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/800.css';
import { API } from '../contexts/AuthContext';
import { prepararFoto, reduzirParaEnvio, estiloPadrao, ESTILOS, prepararArte, carregarImagem } from '../helpers/fotoCatalogo';
import { arrumarFolhaPoster, arrumarPeriodoSolto } from '../helpers/posterFolha';
import {
  PROP, MAX_PRODUTOS, DISPOSICOES, disposicaoPadrao, arrumarStory, caixaDoProduto, limitarDeslocamento,
  moverProduto, escalarProduto, tamanhoDaFoto, tamanhoDaFolha, corrigirProporcao, alturaFolha,
} from '../helpers/storyLayout';
import AvisoSugestaoFoto from '../components/AvisoSugestaoFoto';
import AvisoRecorteIA from '../components/AvisoRecorteIA';
import { useUsoPecas, estiloLinhaUsada, MarcasOutrasPecas, LegendaUso } from '../components/UsoPecas';
import PreenchimentoAuto, { gruposDeStory } from '../components/PreenchimentoAuto';
import AvisoIdentidade from '../components/estudio/AvisoIdentidade';
import { esquecerFoto } from '../helpers/recorteIA';

const LADO_FOTO = 1400;          // mesma foto do pôster: um produto sozinho ocupa quase o story todo
// Onde o Instagram cobre o story (foto e nome do perfil em cima, caixa de
// resposta embaixo), em % da altura. Só aparece no editor.
const COBRE_TOPO = 13, COBRE_BAIXO = 10;
const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const uid = () => Math.random().toString(36).slice(2, 10);
const moeda = (v) => Number(v || 0).toFixed(2).replace('.', ',');
const milhar = (s) => String(s).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const moedaMilhar = (v) => { const [i, c] = moeda(v).split(','); return `${milhar(i)},${c}`; };
const limitar = (v, min, max) => Math.min(max, Math.max(min, v));
const r1 = (v) => Math.round(v * 10) / 10;

const fotoPadrao = (p) => ({ origem: p?.fotoPropria ? 'propria' : 'erp', estilo: estiloPadrao(p?.classe),
  tocouEstilo: false, url: null, bruta: null, prop: null, carregando: true, ver: 0, cobre: false });

const novoProduto = (prod) => ({ id: uid(), codpro: prod.codpro, nome: prod.nome, de: prod.de, por: prod.por,
  mostrarDe: false, area: { x: 20, y: 40, w: 60, h: 34 }, folha: { x: 30, y: 65, w: 50 }, ajustado: false });
const novoStory = (padrao) => ({ id: uid(), disposicao: null, produtos: [], topo: { ...padrao.topo }, per: { ...padrao.per } });

const storyParaSalvar = (s) => ({ disposicao: s.disposicao, topo: s.topo, per: s.per,
  produtos: s.produtos.map(p => ({ codpro: p.codpro, nome: p.nome, mostrarDe: p.mostrarDe, area: p.area, folha: p.folha,
    posDe: p.posDe || null, ajustado: p.ajustado })) });

// Depois de mudar de tamanho, a folha não pode ter saído do story: o bloco volta pra dentro.
const dentro = (p, folhaProp) => { const d = limitarDeslocamento(p, 0, 0, folhaProp); return d.dx || d.dy ? moverProduto(p, d.dx, d.dy) : p; };

// Anda com o que está selecionado (setas do teclado).
function moverSelecionado(s, sel, dx, dy, folhaProp) {
  if (sel.tipo === 'produto') {
    return { ...s, produtos: s.produtos.map(p => {
      if (p.id !== sel.id) return p;
      const d = limitarDeslocamento(p, dx, dy, folhaProp);
      return moverProduto(p, d.dx, d.dy);
    }) };
  }
  if (sel.tipo === 'topo') return { ...s, topo: { ...s.topo, x: r1(s.topo.x + dx), y: r1(s.topo.y + dy) } };
  if (sel.tipo === 'periodo') return { ...s, per: { ...s.per, x: r1(limitar(s.per.x + dx, 0, 100)), y: r1(limitar(s.per.y + dy, 0, 100)) } };
  return s;
}

// Corta a margem transparente em volta do recorte: a caixa do produto fica do
// tamanho dele, então arrastar, puxar o canto e encaixar na disposição ficam
// certos. Devolve { url, prop } (prop = altura / largura).
async function apararTransparente(url) {
  const img = await carregarImagem(url);
  const W = img.naturalWidth, H = img.naturalHeight;
  if (!/^data:image\/png/.test(url)) return { url, prop: H / W };
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);
  const d = ctx.getImageData(0, 0, W, H).data;
  let x0 = W, y0 = H, x1 = -1, y1 = -1;
  for (let y = 0; y < H; y++) {
    for (let x = 0, k = y * W * 4 + 3; x < W; x++, k += 4) {
      if (d[k] > 12) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; y1 = y; }
    }
  }
  if (x1 < 0) return { url, prop: H / W };
  const m = Math.round(Math.max(x1 - x0, y1 - y0) * 0.01);   // respiro de 1%
  x0 = Math.max(0, x0 - m); y0 = Math.max(0, y0 - m); x1 = Math.min(W - 1, x1 + m); y1 = Math.min(H - 1, y1 + m);
  const w = x1 - x0 + 1, h = y1 - y0 + 1;
  if (w * h > W * H * 0.97) return { url, prop: H / W };     // quase nada pra cortar
  const out = document.createElement('canvas');
  out.width = w; out.height = h;
  out.getContext('2d').drawImage(c, x0, y0, w, h, 0, 0, w, h);
  return { url: out.toDataURL('image/png'), prop: h / w };
}

const baixarBlob = (blob, nome) => {
  const u = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = u; a.download = nome;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(u), 1000);
};

// Erro de pedido com resposta em blob (o PNG): a mensagem vem num JSON dentro dele.
async function mensagemDeErro(e, padrao) {
  if (!e.response) return `Sem resposta do servidor (${e.message}).`;
  try {
    const dados = e.response.data;
    const corpo = dados instanceof Blob ? JSON.parse(await dados.text()) : dados;
    if (corpo?.error) return corpo.error;
  } catch { /* resposta sem JSON */ }
  return padrao;
}

// `embutido`: aba do Estúdio de Criação (pages/EstudioCampanha.js) — sem o
// cabeçalho próprio, a arte da campanha fica na aba Identidade (`versaoArte` muda
// quando ela troca) e a tela conta pra aba quantos stories tem e se há mudança
// não salva (`onResumo`).
export default function CampanhaStory({ embutido = false, campanhaId, versaoArte = 0, onResumo, onIdentidade } = {}) {
  const { id: idRota } = useParams();
  const id = String(campanhaId || idRota);
  const navigate = useNavigate();
  const [campanha, setCampanha] = useState(null);
  const [modelo, setModelo] = useState(null);         // css, padrões, info das artes (GET /poster/.../story/modelo)
  const [artes, setArtes] = useState(null);           // { fundo, topo } em object URL — o que a prévia desenha
  const [disponiveis, setDisponiveis] = useState([]);
  const [stories, setStories] = useState([]);
  const [fotos, setFotos] = useState({});             // codpro -> foto tratada (a mesma em todos os stories)
  const [selStory, setSelStory] = useState(null);
  const [sel, setSel] = useState(null);               // { tipo: 'produto', id } | { tipo: 'topo' } | { tipo: 'periodo' }
  const [periodo, setPeriodo] = useState('');
  const [busca, setBusca] = useState('');
  const [guias, setGuias] = useState(true);
  const [carregando, setCarregando] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [previa, setPrevia] = useState(null);         // { url, nome }
  const [auto, setAuto] = useState(false);            // janela do preenchimento automático
  const [fontes, setFontes] = useState(0);            // muda quando as fontes terminam de carregar
  const uso = useUsoPecas(id);
  const montado = useRef(true);
  const urlsBrutas = useRef(new Set());
  const folhaProp = modelo?.folhaProp || 1.65;

  // Fontes chegando depois mudam a largura do texto: a folha precisa ser reajustada.
  useEffect(() => {
    let vivo = true;
    document.fonts?.ready.then(() => { if (vivo) setFontes(n => n + 1); });
    const aoCarregar = () => setFontes(n => n + 1);
    document.fonts?.addEventListener?.('loadingdone', aoCarregar);
    return () => { vivo = false; document.fonts?.removeEventListener?.('loadingdone', aoCarregar); };
  }, []);

  // Fundo e topo vêm como imagem (com o token) e viram object URL.
  const carregarModelo = useCallback(async () => {
    const r = await axios.get(`${API}/poster/campanha/${id}/story/modelo`);
    const baixar = async (parte) => {
      const x = await axios.get(`${API}/poster/campanha/${id}/story/arte/${parte}`, { responseType: 'blob' });
      return x.status === 204 || !x.data?.size ? null : URL.createObjectURL(x.data);
    };
    const [fundo, topo] = await Promise.all([baixar('fundo'), baixar('topo')]);
    setArtes(ant => {
      if (ant) Object.values(ant).forEach(u => u && URL.revokeObjectURL(u));
      return { fundo, topo };
    });
    setModelo(r.data);
    return r.data;
  }, [id]);
  const artesAtuais = useRef(null);
  useEffect(() => { artesAtuais.current = artes; }, [artes]);
  useEffect(() => () => { Object.values(artesAtuais.current || {}).forEach(u => u && URL.revokeObjectURL(u)); }, []);
  // A identidade mudou na aba ao lado: redesenha com a arte nova.
  const primeiraArte = useRef(true);
  useEffect(() => {
    if (primeiraArte.current) { primeiraArte.current = false; return; }
    carregarModelo().catch(() => {});
  }, [versaoArte, carregarModelo]);

  // ---- Carga inicial
  useEffect(() => {
    (async () => {
      try {
        const [mod, p, c, l] = await Promise.all([
          carregarModelo(),
          axios.get(`${API}/catalogo/campanha/${id}/produtos`),
          axios.get(`${API}/campanhas/${id}`),
          // Sem os salvos (ex.: tabela ainda não criada), começa vazio em vez de travar a tela.
          axios.get(`${API}/poster/campanha/${id}/story/layout`).catch(() => ({ data: { layout: null }, falhou: true })),
        ]);
        if (l.falhou) Swal.fire({ icon: 'warning', title: 'Não consegui abrir os stories salvos', text: 'Dá pra montar normalmente; confira antes de salvar por cima.', toast: true, position: 'top-end', timer: 4000, showConfirmButton: false });
        const prods = p.data.produtos || [];
        setDisponiveis(prods);
        setCampanha(c.data);
        const salvo = l.data?.layout;
        setPeriodo(salvo?.periodo ?? mod.periodoPadrao ?? '');
        if (salvo?.stories?.length) {
          const porCod = new Map(prods.map(x => [x.codpro, x]));
          const lista = salvo.stories.map(s => {
            const base = novoStory(mod.padrao);
            const salvos = s.produtos || [];
            const produtos = salvos.map(sp => {
              const prod = porCod.get(sp.codpro);
              if (!prod) return null;   // saiu da campanha ou perdeu a aprovação
              return { ...novoProduto(prod), nome: sp.nome || prod.nome, mostrarDe: !!sp.mostrarDe,
                area: { ...sp.area }, folha: { ...sp.folha }, posDe: sp.posDe || null, ajustado: !!sp.ajustado };
            }).filter(Boolean).slice(0, MAX_PRODUTOS);
            const st = { ...base, disposicao: s.disposicao, topo: { ...base.topo, ...s.topo }, per: { ...base.per, ...s.per }, produtos };
            // Perdeu produto: a disposição salva era pra outra quantidade.
            return produtos.length === salvos.length ? st : arrumarStory(st, () => 1, mod.folhaProp, disposicaoPadrao(produtos.length));
          });
          const escolhas = new Map((salvo.fotos || []).map(f => [f.codpro, f.foto || {}]));
          const fs = {};
          lista.forEach(s => s.produtos.forEach(pp => {
            if (fs[pp.codpro]) return;
            const e = escolhas.get(pp.codpro) || {};
            const base = fotoPadrao(porCod.get(pp.codpro));
            fs[pp.codpro] = { ...base, origem: e.origem || base.origem, estilo: e.estilo || base.estilo, tocouEstilo: !!e.tocouEstilo };
          }));
          setFotos(fs);
          setStories(lista);
          setSelStory(lista[0]?.id || null);
        }
      } catch (e) {
        Swal.fire('Erro', e.response?.data?.error || 'Não consegui carregar a campanha.', 'error');
      } finally {
        setCarregando(false);
      }
    })();
  }, [id, carregarModelo]);

  // ---- Fotos: mesma receita do pôster (busca com o token, trata no navegador)
  const buscarFoto = useCallback(async (codpro, origem) => {
    const url = origem === 'propria'
      ? `${API}/catalogo/campanha/${id}/foto/${codpro}`
      : `${API}/produtos/${codpro}/imagem/1`;
    const r = await axios.get(url, { responseType: 'blob' });
    const u = URL.createObjectURL(r.data);
    urlsBrutas.current.add(u);
    return u;
  }, [id]);
  const soltarUrl = (u) => { if (u) { URL.revokeObjectURL(u); urlsBrutas.current.delete(u); esquecerFoto(u); } };

  const tratarFoto = useCallback(async (codpro, f) => {
    try {
      const bruta = f.bruta || await buscarFoto(codpro, f.origem);
      let estilo = f.estilo || 'recorte';
      let r = await prepararFoto(bruta, { estilo, ladoMax: LADO_FOTO });
      if (r.sugestao && !f.tocouEstilo) {
        estilo = r.sugestao;
        r = await prepararFoto(bruta, { estilo, ladoMax: LADO_FOTO });
      }
      const { url, prop } = await apararTransparente(r.url);
      return { ...f, bruta, url, prop, cobre: r.cobre, estilo, carregando: false };
    } catch {
      return { ...f, url: null, carregando: false, erro: true };
    }
  }, [buscarFoto]);

  const mudarFoto = useCallback((codpro, mudanca) => {
    setFotos(prev => {
      const f = prev[codpro];
      if (!f) return prev;
      if ('bruta' in mudanca && f.bruta) soltarUrl(f.bruta);
      return { ...prev, [codpro]: { ...f, ...mudanca, carregando: true, erro: false, ver: (f.ver || 0) + 1 } };
    });
  }, []);

  // Fila: duas fotos por vez; resultado de versão velha é descartado. Foto
  // pronta acerta a caixa do produto em todos os stories onde ele está.
  const emAndamento = useRef(new Set());
  const [fimDeTarefa, setFimDeTarefa] = useState(0);
  useEffect(() => {
    const livres = 2 - emAndamento.current.size;
    if (livres <= 0) return;
    Object.entries(fotos)
      .filter(([cod, f]) => f.carregando && !f.erro && !emAndamento.current.has(`${cod}:${f.ver}`))
      .slice(0, livres)
      .forEach(([cod, f]) => {
        const chave = `${cod}:${f.ver}`;
        emAndamento.current.add(chave);
        tratarFoto(cod, f).then(nova => {
          emAndamento.current.delete(chave);
          if (!montado.current) return;
          setFotos(prev => (prev[cod]?.ver === f.ver ? { ...prev, [cod]: { ...nova, ver: f.ver } } : prev));
          if (nova.prop) setStories(prev => prev.map(s => corrigirProporcao(s, cod, nova.prop, folhaProp)));
          setFimDeTarefa(n => n + 1);
        });
      });
  }, [fotos, tratarFoto, fimDeTarefa, folhaProp]);

  useEffect(() => {
    const urls = urlsBrutas.current;
    montado.current = true;
    return () => {
      montado.current = false;
      urls.forEach(u => { URL.revokeObjectURL(u); esquecerFoto(u); });
      urls.clear();
    };
  }, []);

  // ---- Montagem
  const atual = stories.find(s => s.id === selStory) || null;
  const mudarStory = useCallback((sid, fn) => setStories(prev => prev.map(s => (s.id === sid ? fn(s) : s))), []);
  const propDe = (cod) => fotos[cod]?.prop || 1;

  const noAtual = useMemo(() => new Set(atual?.produtos.map(p => p.codpro) || []), [atual]);
  const emStories = useMemo(() => {
    const n = new Map();
    stories.forEach(s => new Set(s.produtos.map(p => p.codpro)).forEach(c => n.set(c, (n.get(c) || 0) + 1)));
    return n;
  }, [stories]);
  const lista = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return disponiveis.filter(p => !t || p.nome.toLowerCase().includes(t) || p.codpro.includes(t));
  }, [disponiveis, busca]);

  const selecionarStory = (sid) => { setSelStory(sid); setSel(null); };

  const novoStoryVazio = () => {
    const s = novoStory(modelo.padrao);
    setStories(prev => [...prev, s]);
    selecionarStory(s.id);
  };

  // Pôr ou tirar produto arruma o story de novo na disposição da nova quantidade.
  const adicionar = (prod) => {
    if (atual && noAtual.has(prod.codpro)) {
      setSel({ tipo: 'produto', id: atual.produtos.find(p => p.codpro === prod.codpro).id });
      return;
    }
    if (atual && atual.produtos.length >= MAX_PRODUTOS) {
      Swal.fire({ icon: 'info', title: 'Story cheio', text: `Cada story leva até ${MAX_PRODUTOS} produtos. Tire um deles ou crie outro story.` });
      return;
    }
    const np = novoProduto(prod);
    setFotos(prev => (prev[prod.codpro] ? prev : { ...prev, [prod.codpro]: fotoPadrao(prod) }));
    if (!atual) {
      const s = arrumarStory({ ...novoStory(modelo.padrao), produtos: [np] }, propDe, folhaProp, disposicaoPadrao(1));
      setStories(prev => [...prev, s]);
      setSelStory(s.id);
    } else {
      mudarStory(atual.id, s => arrumarStory({ ...s, produtos: [...s.produtos, np] }, propDe, folhaProp, disposicaoPadrao(s.produtos.length + 1)));
    }
    setSel({ tipo: 'produto', id: np.id });
  };
  const tirarProduto = (pid) => {
    mudarStory(atual.id, s => {
      const resto = s.produtos.filter(p => p.id !== pid);
      return arrumarStory({ ...s, produtos: resto }, propDe, folhaProp, disposicaoPadrao(resto.length));
    });
    setSel(null);
  };
  // Frente/trás: a ordem de desenho. O produto fica onde está (não volta pra vaga).
  const ordem = (pid, praFrente) => mudarStory(atual.id, s => {
    const p = s.produtos.find(x => x.id === pid);
    const resto = s.produtos.filter(x => x.id !== pid).map(x => ({ ...x, ajustado: true }));
    return { ...s, produtos: praFrente ? [...resto, { ...p, ajustado: true }] : [{ ...p, ajustado: true }, ...resto] };
  });

  // Preenchimento automático: os mais vendidos sozinhos, depois de 2 em 2 e de
  // 3 em 3, alternando as disposições de cada quantidade.
  const gerarAutomatico = (cfg) => {
    const vez = { 1: 0, 2: 0, 3: 0 };
    const novos = gruposDeStory(cfg.produtos, cfg.sozinhos, cfg.duplas).map(grupo => {
      const n = grupo.length;
      const d = DISPOSICOES[n][vez[n]++ % DISPOSICOES[n].length];
      const produtos = grupo.map(prod => ({ ...novoProduto(prod), mostrarDe: cfg.mostrarDe && prod.de > prod.por }));
      return arrumarStory({ ...novoStory(modelo.padrao), produtos }, propDe, folhaProp, d.nome);
    });
    setFotos(prev => {
      const fs = { ...prev };
      cfg.produtos.forEach(prod => { if (!fs[prod.codpro]) fs[prod.codpro] = fotoPadrao(prod); });
      return fs;
    });
    setStories(prev => (cfg.substituir ? novos : [...prev, ...novos]));
    selecionarStory(novos[0]?.id || null);
    setAuto(false);
  };

  const removerStory = (sid) => {
    const resto = stories.filter(s => s.id !== sid);
    setStories(resto);
    if (selStory === sid) selecionarStory(resto[0]?.id || null);
  };
  const duplicarStory = (sid) => {
    const i = stories.findIndex(s => s.id === sid);
    const s = stories[i];
    const copia = { ...s, id: uid(), produtos: s.produtos.map(p => ({ ...p, id: uid() })) };
    setStories(prev => [...prev.slice(0, i + 1), copia, ...prev.slice(i + 1)]);
    selecionarStory(copia.id);
  };
  const moverStory = (sid, d) => setStories(prev => {
    const i = prev.findIndex(s => s.id === sid), j = i + d;
    if (i < 0 || j < 0 || j >= prev.length) return prev;
    const nova = [...prev];
    [nova[i], nova[j]] = [nova[j], nova[i]];
    return nova;
  });

  // Setas do teclado: ajuste fino do que está selecionado (Shift anda mais).
  const raiz = useRef(null);
  useEffect(() => {
    const tecla = (e) => {
      // Só com a tela à vista: no Estúdio as outras abas ficam montadas, mas escondidas.
      if (!raiz.current?.offsetParent || !atual || !sel || /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;
      const passo = e.shiftKey ? 2 : 0.3;
      const d = { ArrowLeft: [-passo, 0], ArrowRight: [passo, 0], ArrowUp: [0, -passo], ArrowDown: [0, passo] }[e.key];
      if (!d) return;
      e.preventDefault();
      mudarStory(atual.id, s => moverSelecionado(s, sel, d[0], d[1], folhaProp));
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [atual, sel, mudarStory, folhaProp]);

  // O que conta como mudança não salva: posição de produto que a gerente mexeu
  // (o que ainda está no lugar da disposição se acomoda sozinho quando a foto
  // chega) e estilo escolhido à mão (o sugerido pelo recorte também é sozinho).
  const assinatura = useMemo(() => {
    const usados = new Set(stories.flatMap(s => s.produtos.map(p => p.codpro)));
    return JSON.stringify({ periodo,
      s: stories.map(s => [s.disposicao, s.topo, s.per, s.produtos.map(p => [p.codpro, p.nome, p.mostrarDe, p.ajustado ? [p.area, p.folha, p.posDe] : 0])]),
      f: Object.entries(fotos).filter(([c]) => usados.has(c)).map(([c, f]) => [c, f.origem, f.tocouEstilo ? f.estilo : '']) });
  }, [stories, fotos, periodo]);
  const [salvo, setSalvo] = useState(null);
  useEffect(() => { if (!carregando && salvo === null) setSalvo(assinatura); }, [carregando, salvo, assinatura]);
  const sujo = salvo !== null && assinatura !== salvo;
  useEffect(() => { onResumo?.({ qtd: stories.length, sujo }); }, [onResumo, stories.length, sujo]);

  const salvar = async () => {
    setSalvando(true);
    const assinando = assinatura;
    try {
      const usados = [...new Set(stories.flatMap(s => s.produtos.map(p => p.codpro)))];
      await axios.put(`${API}/poster/campanha/${id}/story/layout`, {
        versao: 1, periodo,
        fotos: usados.map(cod => ({ codpro: cod, foto: { origem: fotos[cod]?.origem, estilo: fotos[cod]?.estilo, tocouEstilo: !!fotos[cod]?.tocouEstilo } })),
        stories: stories.map(storyParaSalvar),
      });
      setSalvo(assinando);
      Swal.fire({ icon: 'success', title: 'Stories salvos', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.error || 'Não consegui salvar.', 'error');
    } finally { setSalvando(false); }
  };

  // ---- PNG (o servidor desenha com o mesmo CSS; preço vem do banco)
  const nomeCampanha = (campanha?.nome || 'campanha').replace(/[\\/:*?"<>|]/g, '-');
  const nomeArquivo = (s) => `Story ${stories.findIndex(x => x.id === s.id) + 1} - ${nomeCampanha}.png`;

  const pngDoStory = async (s) => {
    const r = await axios.post(`${API}/poster/campanha/${id}/story/png`, {
      periodo,
      story: {
        topo: s.topo, per: s.per,
        produtos: s.produtos.map(p => ({ codpro: p.codpro, nome: p.nome, mostrarDe: p.mostrarDe, area: p.area, folha: p.folha,
          posDe: p.posDe || null, preencher: !!fotos[p.codpro]?.cobre, foto: fotos[p.codpro]?.url || null })),
      },
    }, { responseType: 'blob' });
    return r.data;
  };

  // Antes de gerar: fotos ainda sendo tratadas param; sem foto, pergunta.
  const prontoPraGerar = async (alvos) => {
    const prods = alvos.flatMap(s => s.produtos);
    if (!prods.length) {
      Swal.fire('Atenção', 'Coloque pelo menos um produto no story.', 'info');
      return false;
    }
    const tratando = new Set(prods.filter(p => fotos[p.codpro]?.carregando && !fotos[p.codpro]?.erro).map(p => p.codpro)).size;
    if (tratando) {
      Swal.fire({ icon: 'info', title: 'Fotos ainda sendo tratadas', text: `Faltam ${tratando} foto(s). Espere terminar e gere de novo.` });
      return false;
    }
    const semFoto = [...new Set(prods.filter(p => !fotos[p.codpro]?.url).map(p => p.codpro))];
    if (semFoto.length) {
      const r = await Swal.fire({ icon: 'warning', title: 'Antes de gerar',
        html: `<div style="text-align:left;font-size:0.9rem"><b>${semFoto.length} produto(s) sem foto</b>: ${semFoto.map(c => `Cód.${c}`).join(', ')}</div>`,
        showCancelButton: true, confirmButtonText: 'Gerar assim', cancelButtonText: 'Voltar' });
      if (!r.isConfirmed) return false;
    }
    return true;
  };

  const verPrevia = async () => {
    if (!atual || !(await prontoPraGerar([atual]))) return;
    setGerando(true);
    Swal.fire({ title: 'Gerando o story...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const blob = await pngDoStory(atual);
      Swal.close();
      const nome = nomeArquivo(atual);
      setPrevia(ant => { if (ant) URL.revokeObjectURL(ant.url); return { url: URL.createObjectURL(blob), nome }; });
    } catch (e) {
      Swal.fire('Erro', await mensagemDeErro(e, 'Não consegui gerar o story.'), 'error');
    } finally { setGerando(false); }
  };
  const fecharPrevia = () => { if (previa) URL.revokeObjectURL(previa.url); setPrevia(null); };

  // Um story baixa o PNG; vários vêm num .zip (PNG já é comprimido: o zip só junta).
  const baixarTodos = async () => {
    const alvos = stories.filter(s => s.produtos.length);
    if (!alvos.length) return Swal.fire('Atenção', 'Monte pelo menos um story com produto.', 'info');
    if (!(await prontoPraGerar(alvos))) return;
    setGerando(true);
    Swal.fire({ title: 'Gerando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const arquivos = {};
      for (let k = 0; k < alvos.length; k++) {
        const titulo = Swal.getTitle();
        if (titulo) titulo.textContent = `Gerando story ${k + 1} de ${alvos.length}...`;
        const blob = await pngDoStory(alvos[k]);
        if (alvos.length === 1) { baixarBlob(blob, nomeArquivo(alvos[k])); break; }
        arquivos[nomeArquivo(alvos[k])] = new Uint8Array(await blob.arrayBuffer());
      }
      if (alvos.length > 1) baixarBlob(new Blob([zipSync(arquivos, { level: 0 })], { type: 'application/zip' }), `Stories - ${nomeCampanha}.zip`);
      Swal.close();
    } catch (e) {
      Swal.fire('Erro', await mensagemDeErro(e, 'Não consegui gerar os stories.'), 'error');
    } finally { setGerando(false); }
  };

  // ---- Arte própria do story (1080 x 1920, já com o logo)
  const trocarArte = async (file) => {
    if (!file) return;
    Swal.fire({ title: 'Enviando a arte...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const arte = await prepararArte(file);
      await axios.post(`${API}/poster/campanha/${id}/arte/story`, arte);
      await carregarModelo();
      Swal.close();
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.error || e.message || 'Não consegui trocar a arte.', 'error');
    }
  };
  const tirarArte = async () => {
    try { await axios.delete(`${API}/poster/campanha/${id}/arte/story`); await carregarModelo(); }
    catch (e) { Swal.fire('Erro', e.response?.data?.error || 'Não consegui tirar a arte.', 'error'); }
  };

  if (carregando || !modelo || !artes) {
    return <div className="p-4"><span className="spinner-border spinner-border-sm me-2"></span>Carregando...</div>;
  }

  const prodSel = sel?.tipo === 'produto' ? atual?.produtos.find(p => p.id === sel.id) : null;
  const porCod = new Map(disponiveis.map(x => [x.codpro, x]));
  const comProduto = stories.filter(s => s.produtos.length).length;

  return (
    <div ref={raiz} style={embutido ? { display: 'flex', flexDirection: 'column', flex: '1 1 0', minHeight: 0 }
      : { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', minHeight: 560 }}>
      {/* O CSS do story é o mesmo do PNG (folha do pôster e fonte do período embutidas). */}
      <style>{modelo.css}</style>

      <div className={embutido ? 'barra-c' : 'd-flex justify-content-between align-items-center flex-wrap gap-2 mb-2'}>
        <div>
          {!embutido && <>
            <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => navigate(`/campanhas/${id}/produtos`)}>
              <i className="bi bi-arrow-left"></i>
            </button>
            <span className="fw-bold" style={{ fontSize: '1.1rem' }}>Stories do Instagram — {campanha?.nome}</span>
          </>}
          <span className={embutido ? 'info' : 'text-muted ms-2'} style={{ fontSize: '0.85rem' }}>
            {stories.length} story(s){sujo && <span className="ms-2" style={{ color: '#A8460A' }}>• não salvo</span>}
          </span>
          <AvisoRecorteIA />
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
          <label className="text-muted" style={{ fontSize: '0.82rem' }}>Período</label>
          <input className="form-control form-control-sm" style={{ width: 210 }} value={periodo}
            onChange={e => setPeriodo(e.target.value.toUpperCase())} placeholder="01 A 30 DE NOVEMBRO" />
          <button className="btn btn-sm btn-outline-secondary" onClick={salvar} disabled={salvando}>
            <i className="bi bi-save me-1"></i>{salvando ? 'Salvando...' : 'Salvar'}
          </button>
          <button className="btn btn-sm btn-outline-success" onClick={verPrevia} disabled={gerando || !atual?.produtos.length}>
            <i className="bi bi-eye me-1"></i>Ver prévia
          </button>
          <button className={`btn btn-sm ${embutido ? 'btn-acao-e' : 'btn-success'}`} onClick={baixarTodos} disabled={gerando || !comProduto}>
            <i className="bi bi-download me-1"></i>Baixar {comProduto > 1 ? `todos (${comProduto}, .zip)` : 'PNG'}
          </button>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 12 }}>
        {/* ---------------- Produtos da campanha ---------------- */}
        <div className="card p-2" style={{ width: 250, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <input className="form-control form-control-sm mb-2" placeholder="Buscar produto..."
            value={busca} onChange={e => setBusca(e.target.value)} />
          <small className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>
            Clique no + pra pôr o produto no story aberto (até {MAX_PRODUTOS}).
          </small>
          <LegendaUso peca="story" />
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {lista.length === 0 && <div className="text-muted p-2" style={{ fontSize: '0.84rem' }}>Nenhum produto aprovado na campanha.</div>}
            {lista.map(p => (
              <div key={p.codpro} className="d-flex align-items-center gap-2 p-1 border-bottom"
                style={estiloLinhaUsada(emStories.has(p.codpro), noAtual.has(p.codpro))}
                title={emStories.has(p.codpro) ? `Em ${emStories.get(p.codpro)} story(s)${noAtual.has(p.codpro) ? ', inclusive o aberto' : ''}` : undefined}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="fw-semibold text-truncate" style={{ fontSize: '0.8rem' }}>{p.nome}</div>
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                    Cód.{p.codpro} · R$ {fmt(p.por)}
                    {!p.temFoto && <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.6rem' }}>sem foto</span>}
                    {emStories.get(p.codpro) > 1 && <span className="ms-1 text-success fw-semibold" style={{ fontSize: '0.68rem' }}>{emStories.get(p.codpro)}×</span>}
                    <MarcasOutrasPecas uso={uso} codpro={p.codpro} peca="story" />
                  </div>
                </div>
                {noAtual.has(p.codpro)
                  ? <button className="btn btn-sm btn-link p-0 text-secondary" title="Já está neste story — selecionar" onClick={() => adicionar(p)}>
                      <i className="bi bi-check2-square"></i></button>
                  : <button className="btn btn-sm btn-link text-success p-0" title="Pôr no story" onClick={() => adicionar(p)}>
                      <i className="bi bi-plus-lg"></i></button>}
              </div>
            ))}
          </div>
        </div>

        {/* ---------------- Stories montados ---------------- */}
        <div style={{ width: 118, flexShrink: 0, overflowY: 'auto', paddingRight: 2 }}>
          <button className="btn btn-sm btn-warning w-100 mb-1" style={{ fontSize: '0.74rem' }} onClick={() => setAuto(true)}
            title="Monta vários stories de uma vez, com destaque pra quem mais vende">
            <i className="bi bi-magic me-1"></i>Automático
          </button>
          <button className="btn btn-sm btn-outline-primary w-100 mb-2" style={{ fontSize: '0.74rem' }} onClick={novoStoryVazio}>
            <i className="bi bi-plus-lg me-1"></i>Novo story
          </button>
          {stories.length === 0 && <div className="text-muted text-center p-2" style={{ fontSize: '0.78rem' }}>Nenhum story ainda.</div>}
          {stories.map((s, i) => (
            <div key={s.id} className="mb-2" style={{ cursor: 'pointer' }} onClick={() => selecionarStory(s.id)}>
              <div style={{ outline: s.id === selStory ? '3px solid #F16A0D' : '1px solid #ccc', borderRadius: 3, overflow: 'hidden' }}>
                <StoryArte s={s} modelo={modelo} artes={artes} fotos={fotos} periodo={periodo} largura={106} fontes={fontes} />
              </div>
              <div className="d-flex align-items-center justify-content-between" style={{ fontSize: '0.68rem' }}>
                <span>{i + 1}. {s.produtos.length} prod.</span>
                <span className="d-flex">
                  <button className="btn btn-link btn-sm p-0 text-secondary" disabled={i === 0} title="Subir"
                    onClick={e => { e.stopPropagation(); moverStory(s.id, -1); }}><i className="bi bi-chevron-up"></i></button>
                  <button className="btn btn-link btn-sm p-0 text-secondary" disabled={i === stories.length - 1} title="Descer"
                    onClick={e => { e.stopPropagation(); moverStory(s.id, 1); }}><i className="bi bi-chevron-down"></i></button>
                  <button className="btn btn-link btn-sm p-0 text-secondary" title="Duplicar"
                    onClick={e => { e.stopPropagation(); duplicarStory(s.id); }}><i className="bi bi-copy"></i></button>
                  <button className="btn btn-link btn-sm p-0 text-danger" title="Apagar este story"
                    onClick={e => { e.stopPropagation(); removerStory(s.id); }}><i className="bi bi-trash"></i></button>
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* ---------------- Story aberto ---------------- */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflow: 'auto' }}>
          {atual
            ? <EditorStory s={atual} modelo={modelo} artes={artes} fotos={fotos} periodo={periodo} fontes={fontes}
                sel={sel} onSel={setSel} guias={guias} onGuias={setGuias}
                onMudar={(fn) => mudarStory(atual.id, fn)} />
            : <div className="text-muted mt-5 text-center">
                <i className="bi bi-phone d-block mb-2" style={{ fontSize: '2.5rem' }}></i>
                Clique no + de um produto, em "Novo story" ou em <b>Automático</b> pra começar.
              </div>}
        </div>

        {/* ---------------- Ajustes ---------------- */}
        <div className="card p-2" style={{ width: 290, flexShrink: 0, overflowY: 'auto' }}>
          {prodSel
            ? <AjustesProduto p={prodSel} foto={fotos[prodSel.codpro] || {}} prod={porCod.get(prodSel.codpro)} campanhaId={id} folhaProp={folhaProp}
                onMudar={(fn) => mudarStory(atual.id, s => ({ ...s, produtos: s.produtos.map(p => (p.id === prodSel.id ? fn(p) : p)) }))}
                onFoto={(m) => mudarFoto(prodSel.codpro, m)}
                onOrdem={(frente) => ordem(prodSel.id, frente)}
                onRemover={() => tirarProduto(prodSel.id)}
                onFechar={() => setSel(null)} />
            : atual
              ? <AjustesStory s={atual} artes={artes} padrao={modelo.padrao} temPeriodo={!!periodo}
                  onMudar={(fn) => mudarStory(atual.id, fn)}
                  onDisposicao={(nome) => mudarStory(atual.id, s => arrumarStory(s, propDe, folhaProp, nome))} />
              : <small className="text-muted">Os ajustes do story aparecem aqui.</small>}
          <hr className="my-2" />
          {embutido
            ? <AvisoIdentidade onIdentidade={onIdentidade} />
            : <ArteStory info={modelo.artes || {}} onTrocar={trocarArte} onTirar={tirarArte} />}
        </div>
      </div>

      {auto && (
        <PreenchimentoAuto tipo="story" campanhaId={id} produtos={disponiveis} jaUsados={new Set(emStories.keys())}
          temItens={stories.length > 0} onGerar={gerarAutomatico} onFechar={() => setAuto(false)} />
      )}

      {previa && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 10000, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 16 }}
          onClick={fecharPrevia}>
          <div className="d-flex justify-content-between align-items-center mb-2 gap-3" style={{ color: '#fff' }} onClick={e => e.stopPropagation()}>
            <span className="fw-bold"><i className="bi bi-image me-2"></i>{previa.nome} <small className="fw-normal opacity-75">(1080 × 1920)</small></span>
            <span className="d-flex gap-2">
              <a className="btn btn-sm btn-success" href={previa.url} download={previa.nome}><i className="bi bi-download me-1"></i>Baixar</a>
              <button className="btn btn-sm btn-light" onClick={fecharPrevia}><i className="bi bi-x-lg"></i></button>
            </span>
          </div>
          <img src={previa.url} alt="Prévia do story" onClick={e => e.stopPropagation()}
            style={{ flex: 1, minHeight: 0, maxWidth: '100%', objectFit: 'contain', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// O story em si, na largura pedida. Mesma estrutura do HTML do PNG
// (backend/src/services/storyPng.js › storyHTML) e mesmo CSS.
// `onApertar` (só no editor) recebe o que foi apertado; `sel` destaca o período.
// -----------------------------------------------------------------------------
function StoryArte({ s, modelo, artes, fotos, periodo, largura, fontes, onApertar, sel, children }) {
  const per = useRef(null);
  useLayoutEffect(() => { if (per.current) arrumarPeriodoSolto(per.current); }, [periodo, s.per.s, s.per.mostrar, largura, fontes]);
  const ap = (alvo) => (onApertar ? (e) => onApertar(e, alvo) : undefined);
  const mao = onApertar ? 'move' : undefined;
  const perSel = sel?.tipo === 'periodo';

  return (
    <div className="poster story" onPointerDown={ap(null)}
      style={{ width: largura, height: largura * PROP, '--pw': `${largura / 100}px`, userSelect: 'none' }}>
      <img className="p-fundo" src={artes.fundo} alt="" draggable={false} />
      {artes.topo && s.topo.mostrar && (
        <img className="s-topo" src={artes.topo} alt="" draggable={false} onPointerDown={ap({ tipo: 'topo' })}
          style={{ left: `${s.topo.x}%`, top: `${s.topo.y}%`, width: `${s.topo.w}%`, cursor: mao }} />
      )}
      {s.produtos.map(p => {
        const f = fotos[p.codpro] || {};
        return (
          <Fragment key={p.id}>
            <div className="p-area" onPointerDown={ap({ tipo: 'produto', id: p.id })}
              style={{ left: `${p.area.x}%`, top: `${p.area.y}%`, width: `${p.area.w}%`, height: `${p.area.h}%`, cursor: mao }}>
              {f.url
                ? <img className={f.cobre ? 'preenche' : ''} src={f.url} alt="" draggable={false} />
                : <div className="p-sem-foto">{f.carregando ? <span className="spinner-border spinner-border-sm" /> : 'sem foto'}</div>}
            </div>
            <Folha p={p} largura={largura} fontes={fontes} folhaProp={modelo.folhaProp} cursor={mao}
              onPointerDown={ap({ tipo: 'folha', id: p.id })} onApertarDe={ap({ tipo: 'de', id: p.id })} />
          </Fragment>
        );
      })}
      {periodo && s.per.mostrar && (
        <div className={`s-periodo${s.per.cor === 'claro' ? ' claro' : ''}`} onPointerDown={ap({ tipo: 'periodo' })}
          style={{ left: `${s.per.x}%`, top: `${s.per.y}%`, fontSize: `calc(var(--pw) * ${s.per.s})`, cursor: mao,
            outline: perSel ? '2px dashed #0d6efd' : undefined, outlineOffset: 6 }}>
          <img src={modelo.calendario} alt="" draggable={false} /><span ref={per}>{periodo}</span>
          {perSel && <Alca canto="se" onPointerDown={ap({ tipo: 'periodo-escala' })} fora />}
        </div>
      )}
      {children}
    </div>
  );
}

// Onde a fita do "De:" fica dentro da folha, em % dela (o mesmo do CSS .p-de do pôster).
const DE_PADRAO = { x: 20, y: -13 };

function Folha({ p, largura, fontes, folhaProp, cursor, onPointerDown, onApertarDe }) {
  const ref = useRef(null);
  const alt = p.folha.w / folhaProp;   // altura da folha em % da LARGURA do story
  const [inteiro, cent] = moeda(p.por).split(',');
  // O ajuste mede o texto já desenhado: roda depois de cada mudança que mexe em largura.
  useLayoutEffect(() => {
    if (ref.current) arrumarFolhaPoster(ref.current);
  }, [p.nome, p.por, p.folha.w, p.mostrarDe, p.posDe?.x, p.posDe?.y, largura, fontes]);
  return (
    <div ref={ref} className="p-folha" onPointerDown={onPointerDown}
      style={{ left: `${p.folha.x}%`, top: `${p.folha.y}%`, width: `${p.folha.w}%`,
        height: `calc(var(--pw) * ${alt})`, fontSize: `calc(var(--pw) * ${alt})`, cursor }}>
      {p.mostrarDe && p.de > p.por && (
        <span className="p-de" onPointerDown={onApertarDe}
          style={{ ...(p.posDe ? { left: `${p.posDe.x}%`, top: `${p.posDe.y}%` } : {}), cursor }}>De: R$ {moedaMilhar(p.de)}</span>
      )}
      <span className="p-cod">Cód.{p.codpro}</span>
      <span className="p-nome">{p.nome}</span>
      <span className="p-rs">R$</span>
      <span className="p-int">{milhar(inteiro)}</span>
      <span className="p-cent">,{cent}</span>
      <span className="p-avista">à vista</span>
    </div>
  );
}

// Quadradinho de puxar (canto). `fora`: posicionado em volta do pai, não por % do story.
const CURSOR_CANTO = { no: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', so: 'nesw-resize' };
function Alca({ canto, onPointerDown, fora, x, y }) {
  const lado = 12;
  // `fora`: no canto do contorno do período (outlineOffset 6 + 2 de borda).
  const pos = fora
    ? { right: -8 - lado / 2, bottom: -8 - lado / 2 }
    : { left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' };
  return (
    <div onPointerDown={onPointerDown}
      style={{ position: 'absolute', ...pos, width: lado, height: lado, background: '#fff', border: '2px solid #0d6efd',
        borderRadius: 3, cursor: CURSOR_CANTO[canto], pointerEvents: 'auto', zIndex: 30, touchAction: 'none' }} />
  );
}

// -----------------------------------------------------------------------------
// Story aberto: arrasta produto (inteiro), folha (alça "preço"), topo e período;
// puxa os cantos pra mudar o tamanho. O tamanho acompanha a altura da tela.
// -----------------------------------------------------------------------------
// Zoom do editor: 1 = o story cabe na altura da tela; mais que isso rola.
const ZOOMS_EDITOR = [[1, 'Caber na tela'], [1.25, '125%'], [1.5, '150%'], [2, '200%']];

function EditorStory({ s, modelo, artes, fotos, periodo, fontes, sel, onSel, guias, onGuias, onMudar }) {
  const caixa = useRef(null);
  const palco = useRef(null);
  const [base, setLargura] = useState(360);
  const [zoom, setZoom] = useState(1);
  const [ativo, setAtivo] = useState(null);       // tipo do arrasto em andamento
  const [linhas, setLinhas] = useState([]);       // guias de alinhamento ({ v: x } | { h: y }, em %)
  const [topoProp, setTopoProp] = useState(null); // altura / largura da arte do topo
  const arrasto = useRef(null);
  const folhaProp = modelo.folhaProp;
  const largura = base * zoom;
  const altura = largura * PROP;

  useEffect(() => {
    const medir = () => {
      const el = caixa.current?.parentElement;
      if (!el) return;
      const porAltura = (el.clientHeight - 56) / PROP;
      setLargura(Math.max(240, Math.min(el.clientWidth - 24, porAltura)));
    };
    medir();
    const obs = new ResizeObserver(medir);
    if (caixa.current?.parentElement) obs.observe(caixa.current.parentElement);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!artes.topo) return;
    let vivo = true;
    carregarImagem(artes.topo).then(img => { if (vivo) setTopoProp(img.naturalHeight / img.naturalWidth); }).catch(() => {});
    return () => { vivo = false; };
  }, [artes.topo]);
  const caixaTopo = topoProp ? { x: s.topo.x, y: s.topo.y, w: s.topo.w, h: s.topo.w * topoProp / PROP } : null;

  // Ponto do story (em %) -> posição na tela.
  const naTela = (rect, x, y) => [rect.left + x / 100 * largura, rect.top + y / 100 * altura];

  const apertar = (e, alvo) => {
    if (!alvo) { onSel(null); return; }   // apertou o fundo: tira a seleção
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const rect = palco.current.getBoundingClientRect();
    let ancora = null;
    if (alvo.tipo === 'escala') {
      // Fica parado o canto oposto ao que está sendo puxado.
      const c = caixaDoProduto(s.produtos.find(p => p.id === alvo.id), folhaProp);
      ancora = [alvo.canto.includes('o') ? c.x + c.w : c.x, alvo.canto.includes('n') ? c.y + c.h : c.y];
    } else if (alvo.tipo === 'folha-escala') {
      // Só a folha: o canto oposto dela fica parado.
      const f = s.produtos.find(p => p.id === alvo.id).folha, h = alturaFolha(f.w, folhaProp);
      ancora = [alvo.canto.includes('o') ? f.x + f.w : f.x, alvo.canto.includes('n') ? f.y + h : f.y];
    } else if (alvo.tipo === 'topo-escala' && caixaTopo) {
      ancora = [caixaTopo.x + caixaTopo.w / 2, caixaTopo.y + caixaTopo.h / 2];
    } else if (alvo.tipo === 'periodo-escala') {
      ancora = [s.per.x, s.per.y];
    }
    arrasto.current = { alvo, mx: e.clientX, my: e.clientY, base: s, rect, ancora };
    // Pegou o preço pela alça: a folha fica selecionada (os cantos dela aparecem).
    if (alvo.tipo === 'folha' || alvo.tipo === 'folha-escala') onSel({ tipo: 'produto', id: alvo.id, parte: 'folha' });
    else if (alvo.tipo === 'produto' || alvo.tipo === 'escala' || alvo.tipo === 'de') onSel({ tipo: 'produto', id: alvo.id });
    else if (alvo.tipo.startsWith('topo')) onSel({ tipo: 'topo' });
    else onSel({ tipo: 'periodo' });
    setAtivo(alvo.tipo);
  };

  const mover = (e) => {
    const a = arrasto.current;
    if (!a) return;
    const dx = (e.clientX - a.mx) / largura * 100;
    const dy = (e.clientY - a.my) / altura * 100;
    const { alvo, base: st0 } = a;
    // Quanto o ponteiro se afastou da âncora, comparado ao começo do arrasto.
    const escala = () => {
      const [ax, ay] = naTela(a.rect, a.ancora[0], a.ancora[1]);
      const v0 = [a.mx - ax, a.my - ay], v = [e.clientX - ax, e.clientY - ay];
      const n0 = v0[0] * v0[0] + v0[1] * v0[1];
      return n0 ? Math.max(0.05, (v[0] * v0[0] + v[1] * v0[1]) / n0) : 1;
    };
    // Guias (igual à página livre do catálogo): encosta no meio do story, nas
    // margens do Instagram e nas bordas/meios dos outros produtos.
    const tolX = 6 / largura * 100, tolY = 6 / altura * 100;
    const caixas = (sem) => st0.produtos.filter(p => p.id !== sem).map(p => caixaDoProduto(p, folhaProp));
    const alvosX = (cs) => [50, 4, 96, ...cs.flatMap(o => [o.x, o.x + o.w, o.x + o.w / 2])];
    const alvosY = (cs) => [50, COBRE_TOPO, 100 - COBRE_BAIXO, ...cs.flatMap(o => [o.y, o.y + o.h, o.y + o.h / 2])];
    const encostar = (pontos, alvos, tol) => {
      let melhor = null;
      pontos.forEach(pt => alvos.forEach(t => { const d = t - pt; if (Math.abs(d) <= tol && (!melhor || Math.abs(d) < Math.abs(melhor.d))) melhor = { d, t }; }));
      return melhor;
    };
    // Caixa c deslocada por dx/dy, encostando: devolve o deslocamento ajustado.
    const encaixar = (c, cs) => {
      const sx = encostar([c.x + dx, c.x + c.w + dx, c.x + c.w / 2 + dx], alvosX(cs), tolX);
      const sy = encostar([c.y + dy, c.y + c.h + dy, c.y + c.h / 2 + dy], alvosY(cs), tolY);
      novas.push(...[sx && { v: sx.t }, sy && { h: sy.t }].filter(Boolean));
      return [dx + (sx?.d || 0), dy + (sy?.d || 0)];
    };
    const novas = [];
    const trocar = (id, fn) => {
      const p0 = st0.produtos.find(p => p.id === id);
      if (!p0) return;
      const novo = fn(p0);
      onMudar(st => ({ ...st, produtos: st.produtos.map(p => (p.id === id ? novo : p)) }));
    };

    if (alvo.tipo === 'produto') {
      trocar(alvo.id, p0 => {
        const [ex, ey] = encaixar(caixaDoProduto(p0, folhaProp), caixas(p0.id));
        const d = limitarDeslocamento(p0, ex, ey, folhaProp);
        return moverProduto(p0, d.dx, d.dy);
      });
    } else if (alvo.tipo === 'folha') {
      trocar(alvo.id, p0 => {
        // A folha encosta também na própria foto.
        const f = p0.folha;
        const [ex, ey] = encaixar({ x: f.x, y: f.y, w: f.w, h: alturaFolha(f.w, folhaProp) }, [...caixas(p0.id), p0.area]);
        const d = limitarDeslocamento(p0, ex, ey, folhaProp);
        return { ...p0, ajustado: true, folha: { ...f, x: r1(f.x + d.dx), y: r1(f.y + d.dy) } };
      });
    } else if (alvo.tipo === 'folha-escala') {
      trocar(alvo.id, p0 => {
        const w = limitar(p0.folha.w * escala(), 8, 95), h = alturaFolha(w, folhaProp);
        const [ax, ay] = a.ancora;
        const x = limitar(alvo.canto.includes('o') ? ax - w : ax, 0, 100 - w);
        const y = limitar(alvo.canto.includes('n') ? ay - h : ay, 0, 100 - h);
        return { ...p0, ajustado: true, folha: { x: r1(x), y: r1(y), w: r1(w) } };
      });
    } else if (alvo.tipo === 'de') {
      // Fita do "De:" solta: posição dentro da folha, em % dela.
      trocar(alvo.id, p0 => {
        const d0 = p0.posDe || DE_PADRAO;
        const fw = p0.folha.w / 100 * largura, fh = alturaFolha(p0.folha.w, folhaProp) / 100 * altura;
        return { ...p0, ajustado: true, posDe: { x: r1(limitar(d0.x + (e.clientX - a.mx) / fw * 100, -30, 85)),
          y: r1(limitar(d0.y + (e.clientY - a.my) / fh * 100, -60, 95)) } };
      });
    } else if (alvo.tipo === 'escala') {
      trocar(alvo.id, p0 => dentro(escalarProduto(p0, escala(), a.ancora[0], a.ancora[1]), folhaProp));
    } else if (alvo.tipo === 'topo') {
      const t = st0.topo;
      const [ex, ey] = caixaTopo ? encaixar({ ...caixaTopo, x: t.x, y: t.y }, caixas(null)) : [dx, dy];
      onMudar(st => ({ ...st, topo: { ...st.topo, x: r1(t.x + ex), y: r1(t.y + ey) } }));
    } else if (alvo.tipo === 'topo-escala' && a.ancora) {
      const w = limitar(st0.topo.w * escala(), 10, 160);
      const h = w * topoProp / PROP;
      onMudar(st => ({ ...st, topo: { ...st.topo, w: r1(w), x: r1(a.ancora[0] - w / 2), y: r1(a.ancora[1] - h / 2) } }));
    } else if (alvo.tipo === 'periodo') {
      const [ex, ey] = encaixar({ x: st0.per.x, y: st0.per.y, w: 0, h: 0 }, caixas(null));
      onMudar(st => ({ ...st, per: { ...st.per, x: r1(limitar(st0.per.x + ex, 0, 100)), y: r1(limitar(st0.per.y + ey, 0, 100)) } }));
    } else if (alvo.tipo === 'periodo-escala') {
      const sz = limitar(st0.per.s * escala(), 1.5, 12);
      onMudar(st => ({ ...st, per: { ...st.per, s: Math.round(sz * 100) / 100 } }));
    }
    setLinhas(novas);
  };
  const soltar = () => { arrasto.current = null; setAtivo(null); setLinhas([]); };

  const prodSel = sel?.tipo === 'produto' ? s.produtos.find(p => p.id === sel.id) : null;
  const caixaSel = prodSel ? caixaDoProduto(prodSel, folhaProp) : null;
  const contorno = (c, cor) => (
    <div style={{ position: 'absolute', left: `${c.x}%`, top: `${c.y}%`, width: `${c.w}%`, height: `${c.h}%`,
      outline: `2px dashed ${cor}`, outlineOffset: 2, pointerEvents: 'none', zIndex: 20 }} />
  );
  const cantos = (c, tipo, extra = {}) => [['no', c.x, c.y], ['ne', c.x + c.w, c.y], ['so', c.x, c.y + c.h], ['se', c.x + c.w, c.y + c.h]]
    .map(([canto, x, y]) => (
      <Alca key={canto} canto={canto} x={x} y={y} onPointerDown={(e) => apertar(e, { tipo, canto, ...extra })} />
    ));
  const faixa = { position: 'absolute', left: 0, right: 0, pointerEvents: 'none', zIndex: 15,
    background: 'repeating-linear-gradient(45deg, rgba(13,110,253,0.16) 0 6px, transparent 6px 12px)',
    display: 'flex', justifyContent: 'center', color: '#fff', fontSize: 10, textShadow: '0 0 3px #000' };

  return (
    <div ref={caixa} onPointerMove={mover} onPointerUp={soltar} onPointerCancel={soltar} style={{ touchAction: 'none', paddingTop: 4 }}>
      <div ref={palco} style={{ position: 'relative', width: largura, height: altura, boxShadow: '0 4px 18px rgba(0,0,0,0.25)',
        cursor: ativo ? 'grabbing' : 'default' }}>
        <StoryArte s={s} modelo={modelo} artes={artes} fotos={fotos} periodo={periodo} largura={largura} fontes={fontes}
          onApertar={apertar} sel={sel}>
          {!s.produtos.length && (
            <div style={{ position: 'absolute', left: '10%', right: '10%', top: '45%', zIndex: 10, textAlign: 'center', color: '#fff',
              background: 'rgba(0,0,0,0.55)', borderRadius: 8, padding: 10, fontSize: 13, pointerEvents: 'none' }}>
              Clique no + de até {MAX_PRODUTOS} produtos na lista ao lado
            </div>
          )}
        </StoryArte>

        {guias && <>
          <div style={{ ...faixa, top: 0, height: `${COBRE_TOPO}%`, alignItems: 'flex-end' }}>o Instagram cobre aqui (perfil)</div>
          <div style={{ ...faixa, bottom: 0, height: `${COBRE_BAIXO}%`, alignItems: 'flex-start' }}>caixa de resposta do Instagram</div>
        </>}
        {linhas.map((g, i) => (
          <div key={i} style={g.v != null
            ? { position: 'absolute', left: `${g.v}%`, top: 0, bottom: 0, borderLeft: '1px dashed #F16A0D', zIndex: 25, pointerEvents: 'none' }
            : { position: 'absolute', top: `${g.h}%`, left: 0, right: 0, borderTop: '1px dashed #F16A0D', zIndex: 25, pointerEvents: 'none' }} />
        ))}

        {caixaSel && <>
          {contorno(caixaSel, '#0d6efd')}
          {cantos(caixaSel, 'escala', { id: prodSel.id })}
          {/* Alça pra mexer só a folha (o preço anda em volta da foto) */}
          <div onPointerDown={(e) => apertar(e, { tipo: 'folha', id: prodSel.id })} title="Arraste pra mudar só o preço de lugar"
            style={{ position: 'absolute', zIndex: 30, left: `${prodSel.folha.x + prodSel.folha.w / 2}%`,
              top: `${prodSel.folha.y + alturaFolha(prodSel.folha.w, folhaProp)}%`, transform: 'translate(-50%, 6px)',
              background: '#F16A0D', color: '#fff', fontSize: 11, fontWeight: 600, borderRadius: 10, padding: '1px 8px',
              cursor: 'move', whiteSpace: 'nowrap', touchAction: 'none', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
            <i className="bi bi-arrows-move me-1"></i>preço
          </div>
          {sel?.parte === 'folha' && (() => {
            const f = prodSel.folha, cf = { x: f.x, y: f.y, w: f.w, h: alturaFolha(f.w, folhaProp) };
            return <>{contorno(cf, '#F16A0D')}{cantos(cf, 'folha-escala', { id: prodSel.id })}</>;
          })()}
        </>}
        {sel?.tipo === 'topo' && caixaTopo && artes.topo && s.topo.mostrar && <>
          {contorno(caixaTopo, '#0d6efd')}
          {cantos(caixaTopo, 'topo-escala')}
        </>}
      </div>
      <div className="d-flex justify-content-between align-items-center mt-1 gap-2 flex-wrap" style={{ width: base, fontSize: '0.72rem' }}>
        <span className="text-muted"><i className="bi bi-arrows-move me-1"></i>Arraste e puxe os cantos · setas ajustam fino</span>
        <select className="form-select form-select-sm py-0" style={{ width: 'auto', fontSize: '0.72rem' }} value={zoom}
          title="Zoom do editor" onChange={e => setZoom(Number(e.target.value))}>
          {ZOOMS_EDITOR.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </select>
        <label className="text-muted d-flex align-items-center gap-1" style={{ whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={guias} onChange={e => onGuias(e.target.checked)} />margens do Instagram
        </label>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Ajustes do story aberto (nada selecionado): disposição, topo, período.
// -----------------------------------------------------------------------------
function AjustesStory({ s, artes, padrao, temPeriodo, onMudar, onDisposicao }) {
  const n = s.produtos.length;
  const rotulo = { fontSize: '0.8rem', fontWeight: 600 };
  return (
    <>
      <div className="fw-bold mb-1" style={{ fontSize: '0.9rem' }}>Story com {n} produto{n === 1 ? '' : 's'}</div>
      {n === 0
        ? <small className="text-muted d-block mb-2">Clique no + de até {MAX_PRODUTOS} produtos na lista da esquerda.</small>
        : <>
            <label className="form-label mb-1" style={rotulo}>Disposição</label>
            <div className="d-flex gap-1 flex-wrap mb-1">
              {DISPOSICOES[n].map(d => (
                <button key={d.nome} type="button" className={`btn btn-sm ${s.disposicao === d.nome ? 'btn-warning' : 'btn-outline-secondary'}`}
                  onClick={() => onDisposicao(d.nome)}>{d.rotulo}</button>
              ))}
            </div>
            <small className="text-muted d-block mb-2" style={{ fontSize: '0.7rem' }}>
              Escolher a disposição (ou pôr/tirar produto) recoloca os produtos no lugar dela.
            </small>
            <small className="text-muted d-block mb-2" style={{ fontSize: '0.74rem' }}>
              <i className="bi bi-hand-index me-1"></i>Clique num produto pra mexer nele: arraste o bloco, puxe os cantos e use a alça <b>preço</b> pra mudar só a folha.
            </small>
          </>}

      {artes.topo && <>
        <div className="d-flex align-items-center justify-content-between">
          <div className="form-check mb-0">
            <input className="form-check-input" type="checkbox" id={`topo-${s.id}`} checked={s.topo.mostrar}
              onChange={e => onMudar(q => ({ ...q, topo: { ...q.topo, mostrar: e.target.checked } }))} />
            <label className="form-check-label" htmlFor={`topo-${s.id}`} style={{ fontSize: '0.82rem' }}>Arte do topo</label>
          </div>
          <button className="btn btn-link btn-sm p-0" style={{ fontSize: '0.74rem' }}
            onClick={() => onMudar(q => ({ ...q, topo: { ...padrao.topo, mostrar: q.topo.mostrar } }))}>voltar pro lugar</button>
        </div>
        <small className="text-muted d-block mb-2" style={{ fontSize: '0.7rem' }}>Clique no topo pra arrastar e mudar o tamanho.</small>
      </>}

      {temPeriodo && <>
        <div className="d-flex align-items-center justify-content-between">
          <div className="form-check mb-0">
            <input className="form-check-input" type="checkbox" id={`per-${s.id}`} checked={s.per.mostrar}
              onChange={e => onMudar(q => ({ ...q, per: { ...q.per, mostrar: e.target.checked } }))} />
            <label className="form-check-label" htmlFor={`per-${s.id}`} style={{ fontSize: '0.82rem' }}>Período</label>
          </div>
          <button className="btn btn-link btn-sm p-0" style={{ fontSize: '0.74rem' }}
            onClick={() => onMudar(q => ({ ...q, per: { ...padrao.per, mostrar: q.per.mostrar, cor: q.per.cor } }))}>voltar pro lugar</button>
        </div>
        <div className="d-flex gap-1 align-items-center mb-1">
          <small className="text-muted" style={{ fontSize: '0.74rem' }}>Cor:</small>
          {[['escuro', 'escura'], ['claro', 'clara']].map(([v, t]) => (
            <button key={v} type="button" className={`btn btn-sm py-0 ${s.per.cor === v ? 'btn-warning' : 'btn-outline-secondary'}`}
              style={{ fontSize: '0.74rem' }} onClick={() => onMudar(q => ({ ...q, per: { ...q.per, cor: v } }))}>{t}</button>
          ))}
        </div>
        <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem' }}>Clara pra deixar o período em cima de fundo escuro.</small>
      </>}
    </>
  );
}

// -----------------------------------------------------------------------------
// Ajustes do produto selecionado
// -----------------------------------------------------------------------------
function AjustesProduto({ p, foto, prod, campanhaId, folhaProp, onMudar, onFoto, onOrdem, onRemover, onFechar }) {
  const inputFoto = useRef(null);
  const bloco = caixaDoProduto(p, folhaProp);

  // O bloco inteiro cresce/encolhe em volta do próprio centro.
  const tamanhoBloco = (w) => onMudar(q => {
    const c = caixaDoProduto(q, folhaProp);
    return dentro(escalarProduto(q, w / c.w, c.x + c.w / 2, c.y + c.h / 2), folhaProp);
  });

  const trocarFoto = async (file) => {
    if (!file) return;
    try {
      const { mime, dados } = await reduzirParaEnvio(file);
      await axios.post(`${API}/catalogo/campanha/${campanhaId}/foto/${p.codpro}`, { mime, dados });
      onFoto({ origem: 'propria', bruta: null });
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.error || e.message || 'Não consegui trocar a foto.', 'error');
    } finally {
      if (inputFoto.current) inputFoto.current.value = '';
    }
  };
  const voltarErp = async () => {
    try { await axios.delete(`${API}/catalogo/campanha/${campanhaId}/foto/${p.codpro}`); } catch { /* já não existia */ }
    onFoto({ origem: 'erp', bruta: null });
  };

  const rotulo = { fontSize: '0.8rem', fontWeight: 600 };
  const deslizar = (titulo, valor, min, max, fn) => (
    <>
      <label className="form-label mb-0" style={rotulo}>{titulo} <span className="text-muted">({Math.round(valor)}%)</span></label>
      <input type="range" className="form-range" min={min} max={max} step="0.5" value={limitar(valor, min, max)}
        onChange={e => fn(Number(e.target.value))} />
    </>
  );
  return (
    <>
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <div className="fw-bold" style={{ fontSize: '0.9rem' }}>Cód.{p.codpro}</div>
          <small className="text-muted d-block mb-2">De R$ {fmt(p.de)} · Por R$ {fmt(p.por)}</small>
        </div>
        <button className="btn btn-sm btn-link text-secondary p-0" title="Voltar pros ajustes do story" onClick={onFechar}>
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      <label className="form-label mb-1" style={rotulo}>Texto do produto</label>
      <textarea className="form-control form-control-sm mb-2" rows={2} value={p.nome}
        onChange={e => onMudar(q => ({ ...q, nome: e.target.value }))} />

      {deslizar('Tamanho do produto', bloco.w, 15, 100, tamanhoBloco)}
      {deslizar('Só a foto', p.area.w, 5, 120, (w) => onMudar(q => tamanhoDaFoto(q, w)))}
      {deslizar('Só o preço', p.folha.w, 8, 95, (w) => onMudar(q => tamanhoDaFolha(q, w, folhaProp)))}

      <div className="form-check mb-2">
        <input className="form-check-input" type="checkbox" id={`de-${p.id}`} checked={!!p.mostrarDe}
          disabled={!(p.de > p.por)} onChange={e => onMudar(q => ({ ...q, mostrarDe: e.target.checked }))} />
        <label className="form-check-label" htmlFor={`de-${p.id}`} style={{ fontSize: '0.82rem' }}>
          Mostrar "De: R$ {fmt(p.de)}"
        </label>
        {p.posDe && p.mostrarDe && (
          <button type="button" className="btn btn-link btn-sm p-0 ms-2" style={{ fontSize: '0.74rem' }}
            onClick={() => onMudar(q => ({ ...q, posDe: null }))}>"De:" no lugar</button>
        )}
      </div>

      <label className="form-label mb-1" style={rotulo}>Estilo da foto</label>
      <div className="d-flex gap-1 flex-wrap mb-1">
        {ESTILOS.map(e => (
          <button key={e.valor} type="button"
            className={`btn btn-sm ${foto.estilo === e.valor ? 'btn-warning' : 'btn-outline-secondary'}`}
            onClick={() => onFoto({ estilo: e.valor, tocouEstilo: true })}>{e.rotulo}</button>
        ))}
      </div>
      <small className="text-muted d-block mb-2" style={{ fontSize: '0.7rem' }}>A foto e o estilo valem pra todos os stories com este produto.</small>

      <div className="d-flex gap-2 flex-wrap mb-2">
        <input ref={inputFoto} type="file" accept="image/jpeg,image/png,image/webp" hidden
          onChange={e => trocarFoto(e.target.files?.[0])} />
        <button className="btn btn-sm btn-outline-primary" onClick={() => inputFoto.current?.click()}>
          <i className="bi bi-upload me-1"></i>Trocar foto
        </button>
        {foto.origem === 'propria' && (
          <button className="btn btn-sm btn-outline-secondary" onClick={voltarErp}>
            <i className="bi bi-arrow-counterclockwise me-1"></i>Voltar à do sistema
          </button>
        )}
      </div>
      <AvisoSugestaoFoto sugestao={prod?.sugestao} />

      <div className="d-flex gap-1 mb-2">
        <button className="btn btn-sm btn-outline-secondary flex-fill" onClick={() => onOrdem(true)} title="Desenhar por cima dos outros">
          <i className="bi bi-front me-1"></i>Pra frente
        </button>
        <button className="btn btn-sm btn-outline-secondary flex-fill" onClick={() => onOrdem(false)} title="Desenhar por baixo dos outros">
          <i className="bi bi-back me-1"></i>Pra trás
        </button>
      </div>
      <button className="btn btn-sm btn-outline-danger" onClick={onRemover}>
        <i className="bi bi-trash me-1"></i>Tirar do story
      </button>
    </>
  );
}

// -----------------------------------------------------------------------------
// Arte própria do story: a tela inteira já pronta (com o logo). Com ela, o topo
// sai. Sem ela, vale o fundo/topo do pôster da campanha (ou o padrão).
// -----------------------------------------------------------------------------
function ArteStory({ info, onTrocar, onTirar }) {
  const input = useRef(null);
  const a = info.story;
  let qualidade = null;
  if (a?.largura) {
    const fora = Math.abs((a.altura / a.largura) / PROP - 1) > 0.02;
    const baixa = a.largura < 1000;
    qualidade = (
      <small className="d-block" style={{ fontSize: '0.7rem', color: baixa ? '#dc3545' : '#198754' }}>
        {a.largura}×{a.altura}px{a.bytes ? ` · ${(a.bytes / 1048576).toFixed(1)} MB` : ''}{baixa ? ' — abaixo de 1080 de largura, vai sair suave' : ''}
        {fora && <span className="d-block text-danger">Não está em 9:16 (1080 × 1920): as bordas vão ser cortadas pra caber.</span>}
      </small>
    );
  }
  return (
    <>
      <div className="d-flex align-items-center justify-content-between">
        <span className="fw-bold" style={{ fontSize: '0.85rem' }}>Fundo do story <small className="text-muted fw-normal">(todos)</small></span>
        <span className="badge" style={{ background: a ? '#19875422' : '#eee', color: a ? '#198754' : '#777' }}>{a ? 'da campanha' : 'padrão'}</span>
      </div>
      <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
        A tela inteira já pronta, com o logo — 1080 × 1920 px. Com ela, a arte do topo sai. Sem ela, vai o fundo e o topo do pôster.
        Se a arte já traz a data, deixe o Período em branco.
      </small>
      {qualidade}
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" hidden
        onChange={e => { onTrocar(e.target.files?.[0]); e.target.value = ''; }} />
      <div className="d-flex gap-1 mt-1">
        <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.74rem' }} onClick={() => input.current?.click()}>
          <i className="bi bi-image me-1"></i>{a ? 'Trocar' : 'Subir'}
        </button>
        {a && <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.74rem' }} onClick={onTirar}>Tirar</button>}
      </div>
    </>
  );
}
